import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ActionSheetController, AlertController, ModalController, Platform, PopoverController } from '@ionic/angular';
import { Subscription } from 'rxjs';

/**
 *  The Platform Back Button Service is used to override android device's back button event
 *  and navigate to supplied forcedUrl instead of navigating to previous/default page
 *  at all times we will only have one instance of back button handler in the app and user has to subscribe/unsubscribe at page level
 */
@Injectable({
  providedIn: 'root',
})
export class PlatformBackButtonService {
  // platform back button subscription
  private backButtonSub: Subscription;

  // Platform back button subscription for disabling the default behaviour.
  private backButtonDisableBehaviourSubscription: Subscription;

  constructor(
    private router: Router,
    private platform: Platform,
    private actionSheetCtrl: ActionSheetController,
    private popoverCtrl: PopoverController,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private location: Location,
  ) {}

  /**
   *  Subscribe to platform back button click events and navigate to supplied forcedUrl instead of navigating to previous page from history.
   *  There is a need for forcefully overriding the default history stack navigation, therefore in whichever the component it is required
   *  need to subscribe to the to the back button in the 'ionViewDidEnter' lifecycle and also need to unsubscribe properly in the 'ionViewDidLeave' lifecycle
   *  which will limit the scope of event to the component level only and not propogate if/when visiting any other pages.
   *
   *  Example: From the guided search when we navigate to the search results page, it should expect to redirect to triage page instead of navigating back to the previous history to guided search.
   *
   *  @param forcedUrl - url to navigate to.
   */
  async addForcedRedirection(forcedUrl: string) {
    // subscribe to platform back button click events
    this.backButtonSub = this.platform.backButton.subscribeWithPriority(9999, async () => {
      // if there is already a subcription on the back button and also a alert/actionsheet/popover/modal is open then hitting back button should not
      // perform the navigation instead need to dismiss the overlay, therefore based on the priority will check if there is an overlay present then dismiss it
      // NOTE: Please make sure not to change the order of the priority as it is based on the superiority
      const isOverlayOpen =
        (await this.alertCtrl.getTop()) ||
        (await this.actionSheetCtrl.getTop()) ||
        (await this.popoverCtrl.getTop()) ||
        (await this.modalCtrl.getTop());

      if (isOverlayOpen) {
        isOverlayOpen.dismiss();
        return;
      }

      // in case there is no forecedUrl to navigate to then the platform back button will behave as usual navigating to previous page from history
      if (forcedUrl) {
        this.router.navigate([forcedUrl]);
      } else {
        this.location.back();
      }
    });
  }

  /**
   * Disables the platform's hardware back button for modals to ensure users interact only through the provided action buttons.
   * The modal component should subscribe to the back button event in the 'ionViewDidEnter' lifecycle hook and unsubscribe in 'ionViewDidLeave'.
   * This ensures the back button handling is scoped to the modal only and does not affect other pages or components.
   *
   * Example: In client intervention workflows, users must select an available option to close the modal, enforcing a deliberate user decision.
   */
  disableHardwareBackButtonDefaultBehaviour() {
    // Disable hardware back button while modal is open
    this.backButtonDisableBehaviourSubscription = this.platform.backButton.subscribeWithPriority(9999, () => {
      // do nothing here to disable back button
    });
  }

  /**
   * Enable platform back button default behaviour.
   */
  enableHardwareBackButtonDefaultBehaviour() {
    this.backButtonDisableBehaviourSubscription?.unsubscribe();
  }

  /**
   * Unsubscribe the platform back button subscription
   */
  removeForcedRedirection() {
    this.backButtonSub.unsubscribe();
  }
}
